/**
 * Semantic color tokens — intention-named aliases over the raw ramps in
 * `tokens.ts`. Components reference *meaning* (e.g. `fg.muted`, `status.error`)
 * rather than a specific hue, which keeps the system themeable (a future dark
 * mode becomes a `_dark` swap here, not a component rewrite).
 *
 * Phase 1 contract: every name introduced here is NEW — no current component
 * references these tokens — so adding them to the Chakra theme is render-neutral.
 * They are consumed by the design-system components built in Phase 2 onward.
 *
 * The shape matches Chakra v2's `semanticTokens.colors` config. Each entry is a
 * `{ default, _dark }` pair; `_dark` mirrors `default` for now (dark mode is an
 * explicit Phase-2+ non-goal, but the structure is ready for it).
 */

import { BACKGROUND, BLUE, GRAY, GREEN, ORANGE, RED, TEAL } from "./tokens";

type SemanticColor = { default: string; _dark: string };

const sameInBothModes = (value: string): SemanticColor => ({
  default: value,
  _dark: value,
});

export const semanticColors: Record<string, SemanticColor> = {
  // Backgrounds (page canvas, raised surfaces, overlay scrims).
  "bg.page": sameInBothModes(BACKGROUND.page),
  "bg.surface": sameInBothModes(BACKGROUND.surface),
  "bg.overlay": sameInBothModes(BACKGROUND.overlay),
  "bg.subtle": sameInBothModes(GRAY[50]),
  "bg.muted": sameInBothModes(GRAY[100]),

  // Foreground / text.
  "fg.default": sameInBothModes(GRAY[800]),
  "fg.muted": sameInBothModes(GRAY[600]),
  "fg.subtle": sameInBothModes(GRAY[500]),
  "fg.onAccent": sameInBothModes("#FFFFFF"),

  // Borders / dividers.
  "border.default": sameInBothModes(GRAY[200]),
  "border.strong": sameInBothModes(GRAY[300]),

  // Brand accent.
  "accent.default": sameInBothModes(TEAL[500]),
  "accent.emphasis": sameInBothModes(TEAL[600]),
  "accent.subtle": sameInBothModes(TEAL[50]),

  // Status — paired fg + subtle bg for badges/banners. Never color-only:
  // components must still carry an icon or text (WCAG color-independence).
  "status.success": sameInBothModes(GREEN[500]),
  "status.successSubtle": sameInBothModes(GREEN[50]),
  "status.warning": sameInBothModes(ORANGE[500]),
  "status.warningSubtle": sameInBothModes(ORANGE[50]),
  "status.error": sameInBothModes(RED[500]),
  "status.errorSubtle": sameInBothModes(RED[50]),
  "status.info": sameInBothModes(BLUE[500]),
  "status.infoSubtle": sameInBothModes(BLUE[50]),
};

export default semanticColors;
